import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Manus AI Navigation System
import { Sidebar, TopBar } from '../components/navigation';
import { MobileAppBar } from '../components/navigation/MobileAppBar';
import { MobileNav } from '../components/MobileNav';
import { FirstRunTour } from '../components/FirstRunTour';
import { SetupChecklist } from '../components/SetupChecklist/SetupChecklist';
import { useInboxStore } from '../stores/inboxStore';
import { useChatStore } from '../stores/chatStore';
import { initNativePush } from '../lib/nativePush';
import { updateAppBadge } from '../lib/appBadge';
import { isImpersonating, exitImpersonation } from '../utils/ghostProtocol';
import { GlobalCommandBar, OPEN_COMMAND_BAR_EVENT } from '../components/GlobalCommandBar';
import { EmailVerificationBanner } from './EmailVerificationBanner';
import { IntegrationReauthBanner } from './IntegrationReauthBanner';
import { ContactCapBanner } from './ContactCapBanner';
import { isPrimaryModPressed } from '../lib/platform';
import { useStandaloneSectionShortcuts } from '../hooks/useStandaloneSectionShortcuts';
import {
  KeyboardShortcutsSheet,
  SHOW_SHORTCUTS_EVENT,
} from '../components/KeyboardShortcutsSheet';

/**
 * AppLayout - Manus AI Style Navigation
 *
 * Clean, light UI with:
 * - Left sidebar (GROWTH/CORE/SYSTEM sections)
 * - TopBar with search and notifications (NO Business Hub button)
 * - Mobile hamburger menu
 * - Business Hub accessed via sidebar only
 */
const SIDEBAR_COLLAPSED_KEY = 'buzzbit_sidebar_collapsed';

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsedPreference);
  const [commandBarOpen, setCommandBarOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const inboxUnread = useInboxStore((s) => s.unread);

  // Native push: register this device for FCM and route notification taps.
  // No-op on web (Capacitor.isNativePlatform() === false).
  useEffect(() => {
    void initNativePush((url) => navigate(url));
  }, [navigate]);

  // The ⌘K palette (and anything else) can open the shortcuts panel without
  // prop drilling by dispatching SHOW_SHORTCUTS_EVENT on window.
  useEffect(() => {
    const open = (): void => setShortcutsOpen(true);
    window.addEventListener(SHOW_SHORTCUTS_EVENT, open);
    return () => window.removeEventListener(SHOW_SHORTCUTS_EVENT, open);
  }, []);

  // Same trick for the command palette, so the header search box can open it.
  useEffect(() => {
    const open = (): void => setCommandBarOpen(true);
    window.addEventListener(OPEN_COMMAND_BAR_EVENT, open);
    return () => window.removeEventListener(OPEN_COMMAND_BAR_EVENT, open);
  }, []);

  // ⌘1…⌘6 section switching in installed app windows (Dock app / desktop
  // PWA) — inert in browser tabs, where those keys switch browser tabs.
  useStandaloneSectionShortcuts();

  // Global keyboard shortcuts. Primary modifier = ⌘ on Apple platforms and
  // Ctrl elsewhere — on a Mac, plain Ctrl+K stays reserved for the native
  // kill-line text-editing shortcut instead of opening the command bar.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Command Bar (⌘K / Ctrl+K) — toggles, like every native palette.
      if (isPrimaryModPressed(e) && e.key === 'k') {
        e.preventDefault();
        setCommandBarOpen((prev) => !prev);
      }
      // Settings — the macOS "Preferences ⌘," convention (Ctrl+, elsewhere).
      if (isPrimaryModPressed(e) && e.key === ',') {
        e.preventDefault();
        navigate('/account');
      }
      // Shortcuts panel (⌘/ — and ⌘? on layouts where that needs Shift),
      // the Help → Keyboard Shortcuts convention every Mac app follows.
      if (isPrimaryModPressed(e) && (e.key === '/' || e.key === '?')) {
        e.preventDefault();
        setShortcutsOpen((prev) => !prev);
      }
      // Escape closes overlays
      if (e.key === 'Escape') {
        setCommandBarOpen(false);
        setSidebarOpen(false);
        setShortcutsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Keep the sidebar "Unified Inbox" unread badge fresh (poll every 60s).
  useEffect(() => {
    useInboxStore.getState().refresh();
    const id = setInterval(() => useInboxStore.getState().refresh(), 60000);
    return () => clearInterval(id);
  }, []);

  // Warm BuzzTalk once for the shell lifetime so unread badges and /chat open
  // without a cold bootstrap. Teardown runs on logout (not on leaving /chat).
  useEffect(() => {
    void useChatStore.getState().init();
  }, []);

  // Mirror the unread count onto the OS app icon (macOS Dock / Home Screen)
  // when running as an installed app — native apps badge, so do we.
  useEffect(() => {
    void updateAppBadge(inboxUnread);
  }, [inboxUnread]);
  // Clear the badge when the workspace shell unmounts (e.g. logout).
  useEffect(() => () => void updateAppBadge(0), []);

  const setCollapsed = (next: boolean | ((prev: boolean) => boolean)) => {
    setSidebarCollapsed((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, value ? '1' : '0');
      } catch {
        // Ignore quota / private-mode failures — preference is best-effort.
      }
      return value;
    });
  };

  const handleToggleSidebar = () => {
    if (window.innerWidth >= 1024) {
      setCollapsed((prev) => !prev);
    } else {
      setSidebarOpen((prev) => !prev);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        collapsed={sidebarCollapsed}
        onClose={() => setSidebarOpen(false)}
        onToggleCollapse={() => setCollapsed((prev) => !prev)}
      />

      {/* Main Content Area */}
      <div className={`flex min-w-0 flex-1 flex-col transition-[margin] duration-300 ease-in-out ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        {/* TopBar — desktop only; phones get the native app bar below */}
        <div className="hidden lg:block">
          <TopBar onToggleSidebar={handleToggleSidebar} />
        </div>
        <MobileAppBar onToggleSidebar={handleToggleSidebar} />

        {/* Impersonation Banner */}
        {isImpersonating() && (
          <div className="bg-gray-900 text-white px-4 py-2 flex items-center justify-between text-sm">
            <span>👻 You are viewing as another user&apos;s workspace</span>
            <button
              onClick={() => {
                exitImpersonation();
                window.location.href = '/admin';
              }}
              className="bg-white text-gray-900 px-3 py-1 rounded font-medium hover:bg-gray-100 transition-colors"
            >
              Exit Impersonation
            </button>
          </div>
        )}

        {/* Email Verification Banner */}
        <EmailVerificationBanner />

        {/* Expired integration token → reconnect banner (token-expiry sweep) */}
        <IntegrationReauthBanner />

        {/* Contact-based plans: 90% warning / over-cap pause (V46) */}
        <ContactCapBanner />

        {/* Page Content — bottom padding on mobile so the bottom-nav doesn't
            cover it; extra bottom padding while the setup pill docks (§4) */}
        <main className="flex-1 overflow-y-auto pb-16 lg:pb-0 [.setup-dock_&]:pb-28 lg:[.setup-dock_&]:pb-24">
          {children}
        </main>
      </div>

      {/* First-run tour — owners only, server-persisted dismissal (T10) */}
      <FirstRunTour />

      {/* Setup checklist — the tour's sequel: kind-specific steps that tick
          themselves off as integrations connect (kind-alignment §4) */}
      <SetupChecklist />

      {/* Mobile bottom navigation (< lg) */}
      <MobileNav />

      {/* Global Command Bar (⌘K) */}
      <GlobalCommandBar isOpen={commandBarOpen} onClose={() => setCommandBarOpen(false)} />

      {/* Keyboard shortcuts panel (⌘/) */}
      <KeyboardShortcutsSheet isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
};
